import type { MetaFunction } from "react-router";
import { Box, Container, Typography } from '@mui/material';

export const meta: MetaFunction = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Psychoterapie",
    "description": "Psychoterapie v Bílovci a okolí. Integrativní přístup kombinující různé terapeutické směry. Terapie pro děti, dospívající, dospělé i rodiny.",
    "url": "https://www.hanajarkovska.cz/psychoterapie",
    "image": "https://www.hanajarkovska.cz/img/profil1.webp",
    "provider": {
      "@type": "Person",
      "name": "Mgr. Hana Jarkovská",
      "jobTitle": "Psychoterapeut ve výcviku",
      "url": "https://www.hanajarkovska.cz"
    },
    "areaServed": [
      { "@type": "City", "name": "Bílovec" },
      { "@type": "City", "name": "Nový Jičín" },
      { "@type": "City", "name": "Ostrava" },
      { "@type": "AdministrativeArea", "name": "Moravskoslezský kraj" }
    ],
    "serviceType": "Psychoterapie",
    "category": "Zdravotní služby",
    "availableChannel": {
      "@type": "ServiceChannel",
      "serviceLocation": {
        "@type": "Place",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Bílovec",
          "postalCode": "743 01",
          "addressCountry": "CZ"
        }
      }
    },
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "priceCurrency": "CZK"
    }
  };

  return [
    { tagName: "link", rel: "canonical", href: "https://www.hanajarkovska.cz/psychoterapie" },
    { title: "Psychoterapie — Mgr. Hana Jarkovská" },
    { name: "description", content: "Psychoterapie v Bílovci a okolí. Integrativní přístup, metoda Knoblocha a Satirové. Terapie pro děti, dospívající, dospělé i rodiny. Online i osobní konzultace." },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "https://www.hanajarkovska.cz/psychoterapie" },
    { property: "og:title", content: "Psychoterapie — Mgr. Hana Jarkovská" },
    { property: "og:description", content: "Psychoterapie v Bílovci a okolí. Integrativní přístup, metoda Knoblocha a Satirové. Terapie pro děti, dospívající, dospělé i rodiny." },
    { property: "og:image", content: "https://www.hanajarkovska.cz/img/profil1.webp" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: "Psychoterapie — Mgr. Hana Jarkovská" },
    { name: "twitter:description", content: "Psychoterapie v Bílovci a okolí. Integrativní přístup, metoda Knoblocha a Satirové. Terapie pro děti, dospívající, dospělé i rodiny." },
    { name: "twitter:image", content: "https://www.hanajarkovska.cz/img/profil1.webp" },
    { "script:ld+json": structuredData },
  ];
};

export default function PsychotherapyPage() {
  return (
    <Box sx={{ bgcolor: 'background.default', py: { xs: 6, md: 12 } }}>
      <Container maxWidth="md">
        <Typography variant="h1" sx={{ mb: 2 }}>
          Psychoterapie
        </Typography>
        <Typography variant="h2" sx={{ fontSize: '1.5rem', fontWeight: 400, mb: 2 }}>
          Když je čas na změnu
        </Typography>

        <Box sx={{ '& > p': { mb: 2.5 } }}>
          <Typography sx={{ textAlign: 'justify' }}>
            Každý si neseme svůj příběh. Některé jeho části známe dobře, jiné zůstávají skryté nebo nedořečené. Někdy se život odvíjí klidně,
            jindy přichází napětí, vnitřní zmatek nebo pocity, kterým nerozumíme. Opakují se situace, které jsme nechtěli znovu zažít, nebo
            reagujeme způsobem, který nás samotné překvapí.
          </Typography>

          <Typography sx={{ textAlign: 'justify' }}>
            Často to nejsou jen naše vlastní zkušenosti, co nás formuje. Příběhy a vzorce, které se předávají napříč generacemi, ovlivňují,
            jak vnímáme sebe, druhé i vztahy, do kterých vstupujeme. Neseme v sobě i to, co přichází z rodiny – pravidla, mlčení a loajality,
            které jsme si nevybrali, ale naučili se s nimi žít. V terapii si všímáme poranění a nevyřčených bolestí, které se dědí, stejně
            jako těch, které vznikají právě teď. Vedle toho objevujeme i vnitřní sílu, která nám pomáhá tyto zkušenosti nést,
            rozumět jim a postupně se s nimi vyrovnávat.
          </Typography>

          <Typography sx={{ textAlign: 'justify' }}>
            Psychoterapie je proces, který umožňuje vidět vlastní příběh novým způsobem – s odstupem, nadhledem a hlubším pochopením souvislostí.
            Pomáhá rozpoznat, co vás drží, co už není potřeba, a kde je prostor pro změnu. Společně hledáme, jak s tím, co přinášíte, by to mohlo
            jít jinak – blíž tomu, kým skutečně jste a co potřebujete. V tomto prostoru se může dít proměna – klidná i hluboká.
          </Typography>

          <Typography sx={{ textAlign: 'justify' }}>
            Nejde o rychlou změnu, ale o cestu, která může přinést víc jasnosti, svobody a klidu. Jde o přístup, jehož účinky potvrzují jak zkušenosti, tak výzkum.
          </Typography>
        </Box>

        <Typography variant="h3" sx={{ fontSize: '1.25rem', fontWeight: 500, mb: 3, mt: 5 }}>
          Jak probíhá terapie
        </Typography>

        <Box sx={{ '& > p': { mb: 2.5 } }}>
          <Typography sx={{ textAlign: 'justify' }}>
            Terapie je prostor, kde se váš příběh může začít znovu odkrývat a získávat nové významy. Tento proces může přinést postupné pochopení
            a umožnit vám vidět věci v jiných spojeních. Proto je důležité vědět, jak může spolupráce probíhat a co od ní můžete očekávat.
          </Typography>

          <Typography sx={{ textAlign: 'justify' }}>
            Sezení obvykle trvá 50 minut. První setkání slouží především k tomu, abychom společně porozuměli tomu, s čím přicházíte.
            Tato úvodní fáze může trvat i několik sezení, protože důležité informace a souvislosti se často odhalují postupně.
            Během této doby se také domlouváme na cílech naší spolupráce a na četnosti setkávání – doporučená frekvence bývá jednou týdně
            nebo jednou za čtrnáct dní.
          </Typography>

          <Typography sx={{ textAlign: 'justify' }}>
            Cíle terapie i frekvence sezení se přitom mohou v průběhu spolupráce přirozeně vyvíjet – podle toho, co se v procesu ukazuje jako podstatné.
          </Typography>
        </Box>

        <Typography variant="h3" sx={{ fontSize: '1.25rem', fontWeight: 500, mb: 3, mt: 5 }}>
          Metody a přístupy
        </Typography>

        <Box sx={{ '& > p': { mb: 2.5 } }}>
          <Typography sx={{ textAlign: 'justify' }}>
            V rámci terapeutického procesu využívám různé metody a přístupy. Jejich společným cílem je podpořit vás v porozumění tomu, co prožíváte,
            v hledání nových způsobů, jak zvládat náročné situace, a také v přijímání sebe sama takových, jací jste. Terapeutická práce často vede
            k hlubšímu uvědomění potřeb, které v sobě nesete, i vzorců, které jste si osvojili – vědomě či nevědomě.
          </Typography>

          <Typography sx={{ textAlign: 'justify' }}>
            Součástí procesu může být i otevření se pocitům, kterým se v běžném životě spíše vyhýbáte, nebo posilování schopnosti využívat vnitřní zdroje,
            které ve vás už dávno jsou, jen třeba nebyly dosud plně rozpoznány.
          </Typography>

          <Typography sx={{ mb: 5, textAlign: 'justify' }}>
            Je na vás, jak daleko chcete jít.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
